import {useState, useEffect, useRef} from 'react'
import {connect, useDispatch} from 'react-redux'
import {Field, autofill, reduxForm, getFormValues, reset, getFormMeta} from 'redux-form'
import {useHistory, useLocation} from 'react-router-dom'
import {get} from 'lodash'
import compose from 'lodash/fp/compose'
import moment from 'moment'
import classNames from 'classnames'
import {ArrowNext, ArrowPrev} from 'jbc-front/components/icons'
import {MainFormControls, FilteringTag} from 'jbc-front/components/SearchForm'
import {getState} from 'utils'
import {actionCreators} from 'actions'
import searchDetector from 'searchDetector'
import {Tab} from 'components/Tabs'
import {SelectProcedureModal} from 'pages/withAuth/adminPage/procedureStatuses/index/parts/SelectProcedureModal'
import {
  formName,
  convertQueryToForm,
  getCurrentQueryFromLocation,
  fetchProcedureStatuses,
  convertFormToQueryString,
  fetchSelections,
  hasConditions,
} from './utils'
import AdditionalSearchFields from './AdditionalSearchFields'
import {ScalarOfficeSelector} from 'features/offices/OfficeSelector'
import {useHasBranchOffice} from 'hooks/useHasBranchOffice'
import styles from './SearchForm.scss'

const procedureTypes = (isOfficeAdmin) => [
  {id: 'all', name: 'すべて'},
  {id: 'enroll', name: '入社の手続き'},
  {id: 'resign', name: '退社の手続き'},
  {id: 'change_address', name: '住所の変更'},
  {id: 'change_name', name: '氏名の変更'},
  {id: 'change_dependents', name: '家族の変更'},
  {id: 'health_insurance_qualification_confirmation', name: '資格確認書交付手続き'},
  {id: 'maternity_leave', name: '産休の手続き'},
  {id: 'childcare_leave', name: '育休の手続き'},
  {id: 'parental_leave', name: '出生時育休の手続き'},
  {id: 'parental_leave_support_allowance', name: '休業支援給付の手続き'},
  ...(!isOfficeAdmin ? [{id: 'change_office', name: '転勤の手続き'}] : []),
]

const statuses = (procedureTypeChangeOffice) => [
  {id: 'all', name: 'すべて'},
  {id: 'draft', name: '途中保存'},
  {id: 'in_progress', name: '対応中'},
  ...(!procedureTypeChangeOffice ? [{id: 'request', name: '依頼中'}] : []),
  ...(!procedureTypeChangeOffice ? [{id: 'applying', name: '承認待ち'}] : []),
  {id: 'done', name: '完了'},
]

const SearchProcedureTypeField = ({input: inputProps, onProcedureTypeChange, isOfficeAdmin}) => {
  const scrollFieldRef = useRef()
  const scrollTargetRef = useRef(0)
  const scrollAnimationRef = useRef(null)

  const stopWheelScrollAnimation = () => {
    if (!scrollAnimationRef.current) return
    cancelAnimationFrame(scrollAnimationRef.current)
    scrollAnimationRef.current = null
  }

  const syncScrollTarget = () => {
    const scrollField = scrollFieldRef.current
    if (!scrollField) return
    scrollTargetRef.current = scrollField.scrollLeft
  }

  const scrollToLeft = () => {
    const scrollField = scrollFieldRef.current
    stopWheelScrollAnimation()
    syncScrollTarget()
    scrollField.scrollBy({left: -scrollField.children[0].clientWidth * 1.75, behavior: 'smooth'})
  }

  const scrollToRight = () => {
    const scrollField = scrollFieldRef.current
    stopWheelScrollAnimation()
    syncScrollTarget()
    scrollField.scrollBy({left: scrollField.children[0].clientWidth * 1.75, behavior: 'smooth'})
  }

  useEffect(() => {
    const el = scrollFieldRef.current
    if (!el) return
    scrollTargetRef.current = el.scrollLeft
    const animate = () => {
      const diff = scrollTargetRef.current - el.scrollLeft
      if (Math.abs(diff) < 0.5) {
        el.scrollLeft = scrollTargetRef.current
        scrollAnimationRef.current = null
        return
      }
      el.scrollLeft += diff * 0.12
      scrollAnimationRef.current = requestAnimationFrame(animate)
    }
    const handleWheel = (e) => {
      if (e.deltaY === 0) return
      const maxScrollLeft = el.scrollWidth - el.clientWidth
      if (maxScrollLeft <= 0) return

      const baseScrollLeft = scrollAnimationRef.current ? scrollTargetRef.current : el.scrollLeft
      const nextTarget = Math.max(0, Math.min(maxScrollLeft, baseScrollLeft + e.deltaY))
      if (nextTarget === baseScrollLeft) return

      e.preventDefault()
      scrollTargetRef.current = nextTarget
      if (!scrollAnimationRef.current) {
        scrollAnimationRef.current = requestAnimationFrame(animate)
      }
    }
    el.addEventListener('wheel', handleWheel, {passive: false})
    return () => {
      el.removeEventListener('wheel', handleWheel)
      stopWheelScrollAnimation()
    }
  }, [])

  return (
    <div className={styles.searchProcedureTypeField}>
      <div className={classNames('l-wrap-xxl', styles.procedureTypeTabField)}>
        <button type="button" className={styles.procedureTypeScrollButton} onClick={scrollToLeft}>
          <ArrowPrev />
        </button>
        <div ref={scrollFieldRef} className={styles.procedureTypeScrollField}>
          {procedureTypes(isOfficeAdmin).map((item) => (
            <Tab
              key={item.id}
              selected={item.id === inputProps.value}
              active={item.id === inputProps.value}
              onClick={(e) => {
                const newValue = item.id
                if (inputProps.value !== newValue) {
                  stopWheelScrollAnimation()
                  syncScrollTarget()
                  inputProps.onChange(newValue)
                  inputProps.onBlur(newValue)
                  onProcedureTypeChange()
                  e.currentTarget.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'nearest'})
                }
              }}
            >
              {item.name}
            </Tab>
          ))}
        </div>
        <button type="button" className={styles.procedureTypeScrollButton} onClick={scrollToRight}>
          <ArrowNext />
        </button>
      </div>
    </div>
  )
}

const SearchStatusField = ({input: inputProps, onStatusChange, procedureTypeChangeOffice}) => (
  <div className={styles.searchStatusField}>
    {statuses(procedureTypeChangeOffice).map((item) => (
      <FilteringTag
        key={item.id}
        selected={item.id === inputProps.value}
        onClick={() => {
          const newValue = item.id
          if (inputProps.value !== newValue) {
            inputProps.onChange(newValue)
            inputProps.onBlur(newValue)
            onStatusChange()
          }
        }}
      >
        {item.name}
      </FilteringTag>
    ))}
  </div>
)

const OfficesSelectBox = ({input: inputProps}) => (
  <div className={styles.officesSelectBox}>
    <ScalarOfficeSelector
      filterByCurrentOffices
      selectedIds={inputProps.value}
      onChange={(value) => {
        inputProps.onChange(value)
        inputProps.onBlur(value)
      }}
    />
  </div>
)

const SearchForm = ({token, formValues, handleSubmit, csvDownloadable, query, isOfficeAdmin}) => {
  const [isExpand, setIsExpand] = useState(false)
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const procedureTypeChangeOffice = query.procedure_type === 'change_office'
  const hasBranchOffice = useHasBranchOffice()

  useEffect(() => {
    fetchSelections(token, dispatch)
    return () => {
      dispatch(actionCreators.fetchDestroy('selections'))
    }
  }, [])

  const clearForm = () => {
    dispatch(reset(formName))
    const officeIds = query.office_ids ? query.office_ids : []
    const search =
      `procedure_type=${query.procedure_type}` + (officeIds.length > 0 ? `&office_ids[]=${officeIds[0]}` : '')
    history.push({...location, search})
  }

  return (
    <div className={styles.searchForm}>
      <form onSubmit={handleSubmit}>
        <Field
          name="procedure_type"
          component={SearchProcedureTypeField}
          onProcedureTypeChange={handleSubmit}
          isOfficeAdmin={isOfficeAdmin}
        />

        <div className="l-wrap-xxl">
          <div className={styles.officesSearchAndProcedureAction}>
            {hasBranchOffice ? <Field name="offices" component={OfficesSelectBox} /> : <div />}
            <SelectProcedureModal
              csvDownloadable={csvDownloadable}
              hasBranchOffice={hasBranchOffice}
              selectedOffice={formValues?.offices}
              query={query}
            />
          </div>

          <div className={styles.mainSearchFields}>
            <Field
              name="status"
              component={SearchStatusField}
              onStatusChange={handleSubmit}
              procedureTypeChangeOffice={procedureTypeChangeOffice}
            />

            <MainFormControls
              isExpand={isExpand}
              hasConditions={hasConditions(formValues)}
              handleClear={clearForm}
              onToggleClick={() => setIsExpand((prevState) => !prevState)}
            />
          </div>
          {isExpand && <AdditionalSearchFields />}
        </div>
      </form>
    </div>
  )
}

const validate = (values) => {
  const errors = {}
  const dateFiled = ['joined_at', 'retired_at', 'birthday', 'change_dependent_at']

  if (values.age_from && values.age_to && +values.age_from > +values.age_to) {
    errors.age_to = errors.age_from = 'の範囲が正しくありません'
  }
  dateFiled.forEach((prefix) => {
    if (
      [values[`${prefix}_start`], values[`${prefix}_end`]].every(
        (date) => date && moment(date, 'YYYY/MM/DD').isValid()
      ) &&
      moment(values[`${prefix}_start`], 'YYYY/MM/DD').diff(moment(values[`${prefix}_end`], 'YYYY/MM/DD'), 'days') > 0
    ) {
      errors[`${prefix}_start`] = errors[`${prefix}_end`] = 'の範囲が正しくありません'
    }
  })

  return errors
}

export default compose(
  searchDetector({
    convertQueryToForm,
    convertFormToQueryString,
    getCurrentQueryFromLocation,
    fetchData(query, additionalParams, dispatch) {
      dispatch(fetchProcedureStatuses(query, additionalParams))
    },
    destroy(dispatch) {
      dispatch(actionCreators.procedureStatuses.list.destroy())
    },
  }),
  connect((state) => ({
    token: state.auth.token,
    formValues: getFormValues(formName)(state),
  })),
  reduxForm({
    form: formName,
    enableReinitialize: true,
    destroyOnUnmount: false,
    validate,
    onSubmit(values, dispatch, {handleSearch}) {
      getState(dispatch).then((state) => {
        const autofilled = get(getFormMeta(formName)(state), 'page.autofilled')
        if (!autofilled) {
          dispatch(autofill(formName, 'page', '1'))
        }
        getState(dispatch).then((state) => {
          handleSearch(getFormValues(formName)(state))
        })
      })
    },
  })
)(SearchForm)
